/**
 * 地图引擎 —— 相机意图、航迹、跟随状态的**唯一**决策点。
 *
 * ## 为什么不叫它 "MapLibre 封装"
 *
 * 这个类里没有一个字提到 MapLibre：它只对着 {@link MapSurface} 这个窄接口说话
 * （"把这块数据放进去""把视野移到这个点"）。这么做换来两件事：
 *
 * 1. 引擎的全部行为（跟随什么时候暂停、换机体时做什么、航迹怎么裁）
 *    可以在没有 WebGL、没有 DOM 的情况下被逐条断言 —— 而它们恰恰是
 *    "地图看起来正常但其实是错的"那类 bug 的所在地；
 * 2. 具体地图库是可以换掉的（MapLibre → 别的）而引擎一行不改。
 *
 * 组件层（`MapCanvas.tsx`）负责把真实地图适配成 `MapSurface`，它因此很薄，
 * 薄到"读一遍就能确认它没做任何决策"。
 *
 * ## 状态吐给界面的方式：快照 + 订阅
 *
 * 界面读到的是 {@link MapSnapshot}（不可变、引用稳定）。不把内部字段直接暴露出去，
 * 是因为"界面上显示的航迹点数"和"内部数组长度"必须是同一份真相 —— 一旦调用方
 * 自己去读 `trail.length`，它就会在不知道"什么时候变了"的情况下渲染出旧值。
 */

import { boundsOf, type GeoBounds, type LatLon } from '@hgc/ardupilot';
import type { VehicleState } from '@hgc/core';
import type { FeatureCollection, LineString, Point } from 'geojson';

import {
  DEFAULT_DEAD_ZONE_FRACTION,
  INITIAL_FOLLOW_STATE,
  followHint,
  followReducer,
  fromUserGesture,
  locateZoom,
  shouldRecenter,
  toLngLatCorners,
  type FollowEvent,
  type FollowState,
} from './follow';
import {
  MAP_SOURCE_IDS,
  headingLengthM,
  homeCollection,
  missingTilesCollection,
  overviewPaddingRatio,
  targetCollection,
  trailCollection,
  vehicleCollection,
  type LineCollection,
  type PointCollection,
  type PolygonCollection,
} from './layers';
import type { BaseMapSource } from './sources';
import {
  DEFAULT_TRAIL_OPTIONS,
  TrailBuffer,
  UpdateGate,
  homePositionOf,
  isMoving,
  trailLengthM,
  vehiclePositionOf,
  type TrailOptions,
  type VehiclePosition,
} from './track';
import type { TileCoord } from './tiles';

export type VehicleCollection = FeatureCollection<Point | LineString, Record<string, unknown>>;

export type MapCorners = readonly [readonly [number, number], readonly [number, number]];

/** 引擎能对地图做的**全部**事情。适配器（MapLibre）只实现这几个动作。 */
export interface MapSurface {
  /** 地图能不能接受相机指令。样式还没解析完时返回 `false`。 */
  isReady(): boolean;
  /** 当前视野包围盒；不可用时为 `null`（此时任何自动跟随都必须放弃，而不是猜） */
  viewBounds(): GeoBounds | null;
  zoom(): number;
  /** 写入某个 GeoJSON 源的数据 */
  setData(sourceId: string, data: FeatureCollection): void;
  /** 把视野移到某个点。`zoom` 由引擎算好（见 `locateZoom`）。 */
  centerOn(point: LatLon, zoom: number): void;
  /** 把视野框到给定角落，`paddingRatio` 是相对视口短边的留白比例 */
  fitCorners(corners: MapCorners, paddingRatio: number): void;
  /** 换底图。实现方负责重建样式，并在样式就绪后把已有数据补回去。 */
  setBasemap(source: BaseMapSource): void;
}

/** 一次数据推送的完整内容。测试与"换底图后补数据"都读它。 */
export interface MapData {
  readonly vehicle: VehicleCollection;
  readonly trail: LineCollection;
  readonly home: PointCollection;
  readonly target: PointCollection;
  readonly coverage: PolygonCollection;
}

export interface MapSnapshot {
  readonly sourceId: string;
  readonly ready: boolean;
  readonly follow: FollowState;
  /** 跟随状态的一句话说明；正在跟随时为 `null` */
  readonly hint: string | null;
  readonly position: VehiclePosition;
  readonly home: LatLon | null;
  readonly trailCount: number;
  readonly trailLengthM: number;
  readonly moving: boolean;
  /** "定位到机体"此刻是否可用 */
  readonly canLocate: boolean;
  /** "全览航迹"此刻是否可用 */
  readonly canOverview: boolean;
}

export interface MapEngineOptions {
  readonly surface: MapSurface;
  readonly source: BaseMapSource;
  /** 初始跟随状态。用户偏好由调用方读出来（见 `settings.ts`） */
  readonly follow?: boolean;
  readonly trailOptions?: TrailOptions;
  readonly deadZoneFraction?: number;
  readonly now?: () => number;
}

export class MapEngine {
  private readonly surface: MapSurface;
  private readonly trail: TrailBuffer;
  private readonly gate = new UpdateGate();
  private readonly deadZone: number;
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();

  private source: BaseMapSource;
  private follow: FollowState;
  private position: VehiclePosition = vehiclePositionOf(null);
  private home: LatLon | null = null;
  private target: LatLon | null = null;
  private headingDeg: number | null = null;
  private moving = false;
  /** 缺失瓦片覆盖层（由覆盖判定推入，见 `setMissingTiles`） */
  private coverageTiles: readonly TileCoord[] = [];
  /** 焦点机体的身份（`systemId`）。换机体时航迹必须清空，见 `update()`。 */
  private identity: number | null = null;
  private forceNext = false;

  private snapshot: MapSnapshot;
  private snapshotStale = true;

  constructor(options: MapEngineOptions) {
    this.surface = options.surface;
    this.source = options.source;
    this.deadZone = options.deadZoneFraction ?? DEFAULT_DEAD_ZONE_FRACTION;
    this.now = options.now ?? ((): number => Date.now());
    this.trail = new TrailBuffer(options.trailOptions ?? DEFAULT_TRAIL_OPTIONS);
    this.follow = followReducer(INITIAL_FOLLOW_STATE, {
      type: 'set-follow',
      follow: options.follow ?? true,
    });
    // 构造即接线：调用方忘记"启动引擎"的表现是地图空白，而那与"没有数据"难以区分
    this.surface.setBasemap(this.source);
    this.pushAll();
    this.snapshot = this.buildSnapshot();
    this.snapshotStale = false;
  }

  /* ───────────────────────── 订阅 ───────────────────────── */

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** 引用稳定：没变化时返回同一个对象（`useSyncExternalStore` 依赖这一点）。 */
  snapshotOf(): MapSnapshot {
    if (this.snapshotStale) {
      this.snapshot = this.buildSnapshot();
      this.snapshotStale = false;
    }
    return this.snapshot;
  }

  dispose(): void {
    this.listeners.clear();
  }

  /* ───────────────────────── 读 ───────────────────────── */

  currentSource(): BaseMapSource {
    return this.source;
  }

  trailPoints(): readonly LatLon[] {
    return this.trail.toArray();
  }

  data(): MapData {
    return {
      vehicle: this.vehicleData(),
      trail: trailCollection(this.trail.toArray()),
      home: homeCollection(this.home),
      target: targetCollection(this.target),
      coverage: missingTilesCollection(this.coverageTiles),
    };
  }

  /**
   * 缺失瓦片覆盖层的数据。
   *
   * 由覆盖判定（`coverage.ts`）算好后推给引擎；引擎只负责把它画出来 ——
   * "哪一片没缓存"是需要联网与缓存清单的判断，不属于相机逻辑。
   */
  setMissingTiles(tiles: readonly TileCoord[]): void {
    if (sameTileList(tiles, this.coverageTiles)) return;
    this.coverageTiles = tiles;
    this.surface.setData(MAP_SOURCE_IDS.coverage, missingTilesCollection(tiles));
    this.notify();
  }

  /** 全览时要框住的区域：优先航迹，"只看过没飞过"时退回到已有的标记点。 */
  overviewBounds(): GeoBounds | undefined {
    const points = this.trail.toArray();
    if (points.length >= 2) return boundsOf(points);
    const markers: LatLon[] = [];
    if (this.position.point !== null) markers.push(this.position.point);
    if (this.home !== null) markers.push(this.home);
    if (this.target !== null) markers.push(this.target);
    return boundsOf(markers);
  }

  /* ───────────────────────── 遥测 ───────────────────────── */

  /**
   * 推一帧遥测。返回"这次有没有真的改动需要重绘的东西"（测试与调试用）。
   *
   * 闸门（`UpdateGate`）在这里把上游帧率与重绘解耦；被丢掉的中间帧**不进航迹** ——
   * 航迹的间距判据（2 m）比闸门间隔更细，绕开闸门只会让界面在上行链路变好时更卡。
   */
  update(state: VehicleState | null, at: number = this.now()): boolean {
    const identity = state === null ? null : state.systemId;
    /*
     * 只有"明确换成**另一架**"才算换机体，两种情况都不算：
     *
     * 1. 第一次收到机体（`previous === null` 且有新身份）—— 否则每个会话的第一帧都会
     *    把用户关掉的跟随开关重新打开（"跟随已关闭"这个偏好会被静默忽略，而且只在
     *    重启后才发现）；
     * 2. 身份变成 `null`（链路掉线、注册表清空）—— 否则一次短暂丢包就把用户刚飞过的
     *    航迹抹掉，而航迹正是回来之后最需要的东西。
     */
    const switchedVehicle =
      identity !== null && this.identity !== null && identity !== this.identity;
    if (identity !== this.identity) {
      this.identity = identity;
      if (switchedVehicle) {
        /*
         * 换机体时清空航迹：两架飞机的轨迹叠在同一张图上，用户看到的是一条
         * "从 A 机位置跳到 B 机位置"的直线，而那条线从未被飞过。
         */
        this.trail.reset();
        this.follow = followReducer(this.follow, { type: 'vehicle-changed' });
      }
      // 新机体的位置对用户是未知的，第一次一定要落数据（并在跟随时把视野带过去）
      this.forceNext = true;
    }

    const position = vehiclePositionOf(state);
    const accepted = this.gate.accept(position.point, at);
    const apply = accepted || this.forceNext;
    this.forceNext = false;

    const home = homePositionOf(state);
    const homeChanged = !sameLatLon(home, this.home);
    const moving = isMoving(state);
    const headingDeg = state?.headingDeg ?? null;

    /*
     * 航向线的出现/消失本身就是一次要重绘的变化：静止时画一条"航向"是在画噪声
     * （见 `isMoving`），所以这两个字段联起来算一次方向变化。
     */
    const directionChanged = moving !== this.moving || headingDeg !== this.headingDeg;

    if (!apply && !homeChanged && !directionChanged && position.status === this.position.status) {
      return false;
    }

    const previous = this.position.point;
    this.moving = moving;
    this.headingDeg = headingDeg;
    // 位置即使没被闸门放行也要记下来：`null`/`no-fix` 这类状态变化必须显示给用户
    this.position = position;
    let trailAppended = false;
    if (apply && position.point !== null) trailAppended = this.trail.push(position.point);
    if (homeChanged) this.home = home;

    this.surface.setData(MAP_SOURCE_IDS.vehicle, this.vehicleData());
    if (trailAppended) this.surface.setData(MAP_SOURCE_IDS.trail, trailCollection(this.trail.toArray()));
    if (homeChanged) this.surface.setData(MAP_SOURCE_IDS.home, homeCollection(this.home));

    const moved = position.point !== null && !sameLatLon(position.point, previous);
    if (this.follow.mode === 'following' && moved && position.point !== null) {
      this.followCamera(position.point);
    }

    this.notify();
    return true;
  }

  /** 目标点（任务/落点）。任务管理器接进来之前只有地图内部的调试入口会用它。 */
  setTarget(target: LatLon | null): void {
    if (sameLatLon(target, this.target)) return;
    this.target = target;
    this.surface.setData(MAP_SOURCE_IDS.target, targetCollection(target));
    this.notify();
  }

  /* ───────────────────────── 相机 ───────────────────────── */

  private followCamera(point: LatLon): void {
    if (!this.surface.isReady()) return;
    const bounds = this.surface.viewBounds();
    // 视野读不出来就不猜：宁可这一帧不动，也不要把相机丢到一个随机的缩放层
    if (bounds === null) return;
    if (shouldRecenter(point, bounds, this.deadZone)) this.surface.centerOn(point, this.surface.zoom());
  }

  /** "定位到机体"。位置不可用时返回 `false`（界面据此给出原因，而不是什么都不发生）。 */
  locate(): boolean {
    const point = this.position.point;
    if (point === null || !this.surface.isReady()) return false;
    this.surface.centerOn(point, locateZoom(this.surface.zoom(), this.zoomRange()));
    this.dispatch({ type: 'locate' });
    return true;
  }

  /** "全览航迹"。没有任何可框内容时返回 `false`。 */
  overview(): boolean {
    if (!this.surface.isReady()) return false;
    const bounds = this.overviewBounds();
    if (bounds === undefined) return false;
    // 只有一个点时 fitBounds 会把视野拉到极近的一层；那种"全览"是没意义的
    if (bounds.northDeg === bounds.southDeg && bounds.westDeg === bounds.eastDeg) return this.locate();
    this.surface.fitCorners(
      toLngLatCorners(bounds),
      overviewPaddingRatio(this.trail.toArray().length),
    );
    this.dispatch({ type: 'overview' });
    return true;
  }

  /** 用户手动平移/缩放。只有**用户手势**会暂停跟随，我们自己发起的相机移动不会。 */
  onMapMove(originalEvent: unknown): void {
    if (fromUserGesture(originalEvent)) this.dispatch({ type: 'user-gesture' });
  }

  setFollow(follow: boolean): void {
    const next = followReducer(this.follow, { type: 'set-follow', follow });
    if (next === this.follow) return;
    this.follow = next;
    // 重新打开跟随时立刻把视野带到机体：等下一个遥测帧会让用户以为开关没生效
    if (next.mode === 'following' && this.position.point !== null) this.followCamera(this.position.point);
    this.notify();
  }

  dispatch(event: FollowEvent): void {
    const next = followReducer(this.follow, event);
    if (next === this.follow) return;
    this.follow = next;
    this.notify();
  }

  setSource(source: BaseMapSource): void {
    if (source.id === this.source.id) return;
    this.source = source;
    this.surface.setBasemap(source);
    // 换底图后源都是刚建出来的（空的），必须把当前数据补回去
    this.pushAll();
    this.notify();
  }

  private zoomRange(): { readonly min: number; readonly max: number } {
    return { min: this.source.minZoom, max: this.source.maxZoom };
  }

  /* ───────────────────────── 内部 ───────────────────────── */

  private vehicleData(): VehicleCollection {
    const point = this.position.point;
    if (point === null) return vehicleCollection(null);
    const heading = this.moving ? this.headingDeg : null;
    const zoom = this.surface.isReady() ? this.surface.zoom() : 0;
    return vehicleCollection(point, {
      headingDeg: heading,
      headingLengthM: heading === null ? 0 : headingLengthM(zoom, point.latDeg),
    });
  }

  private pushAll(): void {
    const data = this.data();
    this.surface.setData(MAP_SOURCE_IDS.vehicle, data.vehicle);
    this.surface.setData(MAP_SOURCE_IDS.trail, data.trail);
    this.surface.setData(MAP_SOURCE_IDS.home, data.home);
    this.surface.setData(MAP_SOURCE_IDS.target, data.target);
    this.surface.setData(MAP_SOURCE_IDS.coverage, data.coverage);
  }

  private notify(): void {
    this.snapshotStale = true;
    for (const listener of [...this.listeners]) listener();
  }

  private buildSnapshot(): MapSnapshot {
    const trail = this.trail.toArray();
    return Object.freeze({
      sourceId: this.source.id,
      ready: this.surface.isReady(),
      follow: this.follow,
      hint: followHint(this.follow),
      position: this.position,
      home: this.home,
      trailCount: trail.length,
      trailLengthM: trailLengthM(trail),
      moving: this.moving,
      canLocate: this.position.point !== null,
      canOverview: this.overviewBounds() !== undefined,
    });
  }
}

function sameLatLon(a: LatLon | null, b: LatLon | null): boolean {
  if (a === null || b === null) return a === b;
  return a.latDeg === b.latDeg && a.lonDeg === b.lonDeg;
}

function sameTileList(a: readonly TileCoord[], b: readonly TileCoord[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((tile, index) => {
    const other = b[index];
    return other !== undefined && tile.z === other.z && tile.x === other.x && tile.y === other.y;
  });
}
